import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { trackEvent } from '../lib/analytics';
import { Button } from '../components/ui/button';
import {
  FileText, Receipt, Users, Send, Download, Shield, CheckCircle, ArrowRight,
  Zap, Clock, Euro, Mic, Sparkles, BarChart3, Mail, ChevronDown, ChevronUp,
  PenLine, CreditCard, Calculator, Globe, Headphones, Star,
  ListTodo, ListChecks, MessageSquare, Calendar, MapPin, Camera, Smartphone,
  Package, UserCheck, CalendarDays, Bell, Lock, Navigation,
} from 'lucide-react';
import DemoCarousel from '../components/DemoCarousel';

// ============== TARIFS ==============
const VAT_RATE = 0.21;
const fmtEur = (n) => new Intl.NumberFormat('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + '\u00A0€';
const PACK_STANDARD_HT = 65;
const MODULE_HT = 25;

const PACK_STANDARD_INCLUDES = [
  'CRM & pipeline',
  'Agenda clients',
  'Devis (dictée vocale)',
  'Factures & relances auto',
  'Facturation mensuelle',
  'Bibliothèque de prix',
  'Export comptable',
  'Trésorerie prévisionnelle',
];

const OPTIONAL_PRICING = [
  { key: 'tasks', icon: ListTodo, title: 'Tâches & Planning', desc: 'Timer, photos, check-lists.' },
  { key: 'stock', icon: Package, title: 'Stocks', desc: 'Produits + alertes.' },
  { key: 'hr', icon: UserCheck, title: 'RH', desc: 'Employés & congés.' },
  { key: 'meetings', icon: MessageSquare, title: 'Réunions IA', desc: 'Résumés automatiques.' },
  { key: 'ai_voice', icon: Mic, title: 'Dictée vocale IA', desc: 'Voix → devis, tâches.' },
  { key: 'team', icon: Lock, title: 'Multi-utilisateurs', desc: 'Équipe & permissions.' },
  { key: 'analytics', icon: BarChart3, title: 'Analytics', desc: 'Stats & journal emails.' },
];


const FEATURES = [
  // Coeur métier
  { icon: PenLine, title: 'Devis personnalisables', desc: 'Modèles élégants, logo, couleurs et conditions sur mesure. PDF en 1 clic.', tag: 'Coeur' },
  { icon: Receipt, title: 'Factures & relances auto', desc: 'Conversion devis → facture instantanée. Rappels de paiement programmés.', tag: 'Coeur' },
  { icon: Mic, title: 'Dictée vocale IA', desc: 'Dictez devis, tâches et réunions. L\'IA structure tout : articles, dates, participants, actions.', tag: 'IA' },
  { icon: Sparkles, title: 'Bibliothèque de prix', desc: 'Vos articles réutilisables avec import/export Excel. Suggestion IA depuis l\'historique.', tag: 'Coeur' },
  { icon: Calculator, title: 'Export comptable', desc: 'FEC, Sage, CEGID, EBP, Winbooks, BOB 50, Excel — votre comptable vous remerciera.', tag: 'Compta' },
  { icon: BarChart3, title: 'Trésorerie prévisionnelle', desc: 'Visualisez le cash attendu sur 30/60/90 jours, récurrences incluses.', tag: 'Pilotage' },

  // CRM & terrain
  { icon: Users, title: 'CRM pipeline Kanban', desc: 'Prospects, clients et opportunités sur un seul écran. Champs personnalisés, timeline complète.', tag: 'CRM' },
  { icon: MapPin, title: 'Multi-adresses + GPS', desc: 'Plusieurs chantiers par client. Bouton « Itinéraire » Google Maps direct sur fiche client et tâche.', tag: 'CRM' },
  { icon: CalendarDays, title: 'Agenda & prise de RDV en ligne', desc: 'Calendrier unifié. Vos clients réservent eux-mêmes via un lien public. Bientôt Google Calendar sync.', tag: 'CRM' },

  // Tâches terrain
  { icon: ListTodo, title: 'Tâches & planning équipe', desc: 'Assignation, machines/équipement, timer chronométré, photos avant/après. Vue calendrier.', tag: 'Terrain' },
  { icon: ListChecks, title: 'Check-lists & notes terrain', desc: 'Liste de points à vérifier + annotations catégorisées (bouchons, info manquante, pièce manquante).', tag: 'Terrain' },
  { icon: Camera, title: 'Photos avant/après', desc: 'Vos ouvriers documentent visuellement chaque intervention. Stocké chiffré.', tag: 'Terrain' },

  // Réunions
  { icon: MessageSquare, title: 'Réunions style Leexi', desc: 'Dictez ou collez vos notes — l\'IA extrait résumé, décisions et actions. Convertissez chaque action en tâche.', tag: 'IA' },

  // Stock & RH
  { icon: Package, title: 'Stock auto-déduit', desc: 'Vos produits se déduisent automatiquement à l\'acceptation du devis. Alertes seuil bas.', tag: 'Stock' },
  { icon: UserCheck, title: 'Module RH', desc: 'Employés, congés payés, exports — démarrage simple, sans payroll complexe.', tag: 'RH' },

  // Facturation
  { icon: Calendar, title: 'Facturation mensuelle consolidée', desc: 'Une seule facture / mois / client regroupant toutes les prestations du mois. Auto-clôture le 1er.', tag: 'Coeur' },
  { icon: Mail, title: 'Emails depuis votre adresse', desc: 'SMTP direct (Gmail, Outlook, OVH...) ou Gmail OAuth. 20 presets auto-détectés.', tag: 'Comms' },

  // Multi-utilisateurs & PWA
  { icon: Lock, title: 'Multi-utilisateurs & permissions', desc: 'Invitez votre équipe par email. Permissions module par module (devis, CRM, tâches…).', tag: 'Équipe' },
  { icon: Bell, title: 'Notifications intelligentes', desc: 'Cloche in-app + emails pour invitations, paiements, échéances, annotations terrain.', tag: 'Pilotage' },
  { icon: Smartphone, title: 'App mobile installable (PWA)', desc: 'Devis.Go s\'installe sur iPhone et Android comme une vraie app. Plein écran, icône, raccourcis.', tag: 'Mobile' },
  { icon: Shield, title: 'Prêt pour Peppol', desc: 'Conformité e-facturation belge intégrée. Activez quand vous voulez.', tag: 'Compta' },
];

const STEPS = [
  { num: '01', title: 'Inscrivez-vous gratuitement', desc: 'Créez votre compte en 30 secondes. 30 jours d\'essai complet, sans carte bancaire.' },
  { num: '02', title: 'Configurez votre identité', desc: 'Logo, coordonnées, couleurs, modèles d\'emails — votre marque, partout.' },
  { num: '03', title: 'Créez votre premier devis', desc: 'Cliquez, dictez ou importez. Envoi par email + PDF en un clic. C\'est parti.' },
];

const FAQ = [
  { q: 'Comment fonctionne la tarification ?', a: 'Le Pack Standard à 65€ HT/mois (CRM, agenda, devis vocaux, factures, bibliothèque, comptabilité) suffit pour démarrer. Vous activez ensuite uniquement les modules dont vous avez besoin (25€ HT/mois chacun). Pas d\'engagement : ajoutez ou retirez à tout moment.' },
  { q: 'Comment fonctionne le mois d\'essai ?', a: 'Vous bénéficiez de 30 jours d\'essai complet à votre inscription : toutes les fonctionnalités, sans carte bancaire ni engagement. À la fin de la période, vous choisissez les modules dont vous avez besoin (Pack Standard 65€ HT/mois + modules optionnels à 25€ HT/mois). Vous gardez vos données dans tous les cas.' },
  { q: 'Mes données sont-elles en sécurité ?', a: 'Oui. Hébergement européen, isolation stricte par compte (multi-tenant), mots de passe chiffrés (bcrypt), connexion HTTPS et conformité RGPD.' },
  { q: 'Puis-je exporter mes données ?', a: 'À tout moment. Vos contacts, devis, factures et bibliothèque sont exportables en Excel. Export comptable au format légal FEC + 6 logiciels comptables (Sage, CEGID, EBP, Winbooks, BOB 50).' },
  { q: 'Compatible avec ma comptabilité ?', a: 'Devis.Go génère les fichiers attendus par votre comptable : FEC normé France, Sage Compta, CEGID Quadratus, EBP, Winbooks et BOB 50 (Belgique). Plus l\'export Excel multi-onglets pour les autres cas.' },
  { q: 'Comment fonctionne la dictée vocale ?', a: 'Vous parlez naturellement ("3 fenêtres double vitrage à 450€, pose comprise"), l\'IA analyse, retrouve vos articles dans la bibliothèque et remplit les lignes. Idéal en mobilité.' },
  { q: 'Y a-t-il une limite d\'utilisation ?', a: 'Aucune. Créez autant de devis, factures et contacts que vous voulez. Si vous atteignez un volume très important (>10 000 documents), contactez-nous, on regardera ensemble.' },
];

const Landing = () => {
  const [stats, setStats] = useState({ users: 0, quotes: 0, invoices: 0, total_invoiced: 0 });
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    trackEvent('page_view', { page_name: 'landing' });
    api.get('/analytics/public-stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  const onCtaRegister = (location) => trackEvent('cta_click_register', { location });
  const onCtaPricing = () => trackEvent('cta_click_pricing');
  const onCtaLogin = () => trackEvent('cta_click_login');

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="landing-page">
      {/* ============== TOP NAV ============== */}
      <header className="fixed top-0 inset-x-0 z-50 bg-white/85 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <img src="/logo-devisgo.png" alt="Devis.Go" className="h-9" />
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-600">
            <a href="#features" className="hover:text-orange-600">Fonctionnalités</a>
            <a href="#how" className="hover:text-orange-600">Comment ça marche</a>
            <a href="#pricing" className="hover:text-orange-600" data-testid="nav-pricing-link">Tarifs</a>
            <a href="#about" className="hover:text-orange-600" data-testid="nav-about-link">À propos</a>
            <a href="#faq" className="hover:text-orange-600">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" onClick={onCtaLogin}>
              <Button variant="ghost" className="text-slate-700" data-testid="nav-login-btn">Connexion</Button>
            </Link>
            <Link to="/register" onClick={() => onCtaRegister('nav')}>
              <Button className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="nav-register-btn">
                Créer un compte
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* ============== HERO ============== */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        {/* Decorative blobs */}
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-orange-200/40 blur-3xl rounded-full" aria-hidden />
        <div className="absolute top-40 -left-32 w-80 h-80 bg-amber-100/60 blur-3xl rounded-full" aria-hidden />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold mb-6 border border-orange-200">
              <Zap className="w-3.5 h-3.5" /> Profitez d&apos;un mois d&apos;essai — sans carte bancaire
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
              Vos <span className="text-orange-500">devis</span> en 2 min,
              <br />
              vos <span className="text-orange-500">factures</span> en 1 clic.
            </h1>
            <p className="text-lg text-slate-600 mt-6 max-w-xl">
              L&apos;outil de gestion commerciale fait pour les indépendants et PME francophones.
              Devis personnalisables, CRM, dictée vocale, export comptable — tout y est.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" onClick={() => onCtaRegister('hero')}>
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-7 py-6 text-base shadow-lg shadow-orange-500/30" data-testid="hero-register-btn">
                  Démarrer mon mois d&apos;essai <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/login" onClick={onCtaLogin}>
                <Button size="lg" variant="outline" className="px-7 py-6 text-base border-slate-300" data-testid="hero-login-btn">
                  J&apos;ai déjà un compte
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-green-500" /> Un mois d&apos;essai offert</span>
              <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-green-500" /> Configuration en 2 min</span>
              <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-green-500" /> 100% en français</span>
            </div>
          </div>

          {/* Hero visual: live product demo carousel */}
          <div className="lg:col-span-6">
            <div className="relative">
              <DemoCarousel />
              {/* Floating badges */}
              <div className="hidden md:flex absolute -left-6 top-12 bg-white rounded-xl border border-slate-200 shadow-xl px-4 py-3 items-center gap-3 z-10">
                <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Devis accepté</p>
                  <p className="font-semibold text-slate-900 text-sm">+ 4 235 €</p>
                </div>
              </div>
              <div className="hidden md:flex absolute -right-4 -bottom-4 bg-white rounded-xl border border-slate-200 shadow-xl px-4 py-3 items-center gap-3 z-10">
                <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Mic className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Devis vocal IA</p>
                  <p className="font-semibold text-slate-900 text-sm">3 articles ajoutés</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== STATS BAR (real-time) ============== */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <Stat label="Utilisateurs" value={stats.users} suffix="" />
          <Stat label="Devis créés" value={stats.quotes} suffix="" />
          <Stat label="Factures émises" value={stats.invoices} suffix="" />
          <Stat label="Facturé via Devis.Go" value={Math.round(stats.total_invoiced)} suffix=" €" />
        </div>
      </section>

      {/* ============== HOW IT WORKS ============== */}
      <section id="how" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <p className="text-orange-600 font-semibold uppercase text-xs tracking-widest">Comment ça marche</p>
            <h2 className="text-3xl lg:text-4xl font-bold mt-2">3 étapes pour vous lancer</h2>
            <p className="text-slate-600 mt-3">Aucune installation, aucune formation. Inscription, configuration, premier devis.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-10">
            {STEPS.map((s) => (
              <div key={s.num} className="relative bg-white rounded-2xl border border-slate-200 p-7 hover:border-orange-300 hover:shadow-md transition">
                <span className="absolute -top-5 left-7 text-5xl font-bold text-orange-500/15">{s.num}</span>
                <h3 className="font-semibold text-lg mt-4">{s.title}</h3>
                <p className="text-slate-600 mt-2 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== FEATURES GRID ============== */}
      <section id="features" className="py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <p className="text-orange-600 font-semibold uppercase text-xs tracking-widest">Fonctionnalités</p>
            <h2 className="text-3xl lg:text-4xl font-bold mt-2">Tout ce qu&apos;il vous faut, vraiment tout</h2>
            <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
              {FEATURES.length} modules, de la création de devis à la gestion d&apos;équipe terrain, en passant par
              l&apos;IA vocale et la compta. Une suite complète, pensée pour les indépendants et PME francophones.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 hover:border-orange-300 hover:shadow-md transition relative" data-testid={`landing-feature-${i}`}>
                {f.tag && (
                  <span className="absolute top-3 right-3 inline-block text-[10px] uppercase tracking-wide font-semibold text-orange-700 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded">
                    {f.tag}
                  </span>
                )}
                <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-orange-100 to-amber-50 flex items-center justify-center mb-4 border border-orange-200/60">
                  <f.icon className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="font-semibold pr-12">{f.title}</h3>
                <p className="text-slate-600 text-sm mt-1.5 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== TARIFS ============== */}
      <section id="pricing" className="py-24 bg-white relative overflow-hidden">
        {/* Decorative orange glow */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" aria-hidden
             style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #f97316 0, transparent 45%), radial-gradient(circle at 80% 70%, #fb923c 0, transparent 45%)' }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <p className="text-orange-600 font-semibold uppercase text-xs tracking-widest">Tarification simple</p>
            <h2 className="text-3xl lg:text-5xl font-bold mt-2 text-slate-900">
              Un pack de base, des modules <span className="text-orange-500">à la carte</span>
            </h2>
            <p className="text-slate-600 mt-4 max-w-xl mx-auto">
              <strong className="text-slate-900">65&nbsp;€ HT/mois</strong> pour tout l&apos;essentiel.
              <span className="mx-1">·</span>
              Modules à la carte : <strong className="text-slate-900">25&nbsp;€ HT/mois</strong>.
              <span className="mx-1">·</span>
              Sans engagement.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            {/* ===== PACK STANDARD — feature card ===== */}
            <div className="lg:col-span-1">
              <div className="relative h-full rounded-3xl border-2 border-orange-500 bg-gradient-to-br from-orange-500 to-orange-600 text-white p-8 shadow-xl shadow-orange-500/20">
                <span className="absolute -top-3 left-8 bg-slate-900 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                  ★ Démarrage recommandé
                </span>
                <div className="flex items-center gap-2 text-orange-100 text-sm font-medium uppercase tracking-wide">
                  <Star className="w-4 h-4 fill-current" />
                  Pack Standard
                </div>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="text-5xl font-bold tracking-tight">{fmtEur(PACK_STANDARD_HT)}</span>
                  <span className="text-orange-100 text-sm">HT / mois</span>
                </div>
                <div className="mt-1 flex items-center gap-3 text-xs text-orange-100">
                  <span>TVA 21% — TTC&nbsp;: {fmtEur(PACK_STANDARD_HT * (1 + VAT_RATE))}</span>
                </div>

                <p className="mt-5 text-orange-50 text-sm leading-relaxed">
                  L&apos;essentiel pour facturer :
                </p>

                <ul className="mt-4 space-y-2.5">
                  {PACK_STANDARD_INCLUDES.map((it) => (
                    <li key={it} className="flex items-start gap-2.5 text-sm">
                      <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-orange-100" />
                      <span className="text-white/95">{it}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/register" onClick={() => onCtaRegister('pricing-pack')} className="block mt-7">
                  <Button size="lg" className="w-full bg-white text-orange-600 hover:bg-orange-50 font-semibold py-6 shadow-lg" data-testid="pricing-pack-cta">
                    Démarrer maintenant <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <p className="mt-3 text-center text-xs text-orange-100">
                  Sans carte bancaire · 1 mois d&apos;essai offert
                </p>
              </div>
            </div>

            {/* ===== MODULES À LA CARTE ===== */}
            <div className="lg:col-span-2">
              <div className="flex items-baseline justify-between mb-5 px-1">
                <h3 className="text-lg font-bold text-slate-900">Modules optionnels</h3>
                <div className="text-xs sm:text-sm text-slate-500">
                  <span className="font-bold text-slate-900">{fmtEur(MODULE_HT)}</span>
                  <span className="ml-1">HT/mois · TVA 21% · TTC&nbsp;{fmtEur(MODULE_HT * (1 + VAT_RATE))}</span>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {OPTIONAL_PRICING.map((m) => (
                  <div
                    key={m.key}
                    className="group relative rounded-2xl border border-slate-200 bg-white p-5 hover:border-orange-300 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                    data-testid={`pricing-module-${m.key}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200/60 flex items-center justify-center shrink-0">
                        <m.icon className="w-5 h-5 text-orange-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-slate-900 leading-tight">{m.title}</h4>
                        <p className="text-xs text-slate-500 mt-1 leading-relaxed line-clamp-2">{m.desc}</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-slate-100 flex items-baseline justify-between">
                      <span className="text-xs uppercase tracking-wide text-slate-400 font-medium">+ à activer</span>
                      <div className="text-right">
                        <span className="text-lg font-bold text-slate-900">{fmtEur(MODULE_HT)}</span>
                        <span className="text-xs text-slate-500 ml-1">HT/mois</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 rounded-2xl bg-slate-50 border border-slate-200 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">30 jours d&apos;essai offerts</p>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Sans carte bancaire, sans engagement.
                    </p>
                  </div>
                </div>
                <Link to="/register" onClick={() => onCtaRegister('pricing-free-banner')} className="shrink-0">
                  <Button variant="outline" size="sm" className="border-orange-300 text-orange-700 hover:bg-orange-50 hover:border-orange-400 whitespace-nowrap" data-testid="pricing-free-cta">
                    Créer mon compte
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* small footnotes */}
          <div className="mt-10 grid sm:grid-cols-3 gap-4 text-center">
            <div className="text-xs text-slate-500">
              <Lock className="w-4 h-4 inline mr-1.5 text-slate-400" />
              Sans engagement · Activez/désactivez à tout moment
            </div>
            <div className="text-xs text-slate-500">
              <Euro className="w-4 h-4 inline mr-1.5 text-slate-400" />
              Facturation mensuelle · TVA 21% Belgique
            </div>
            <div className="text-xs text-slate-500">
              <Shield className="w-4 h-4 inline mr-1.5 text-slate-400" />
              Données hébergées en Europe · RGPD
            </div>
          </div>
        </div>
      </section>

      {/* ============== FREE BANNER ============== */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-10 lg:p-14 text-white text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-20" aria-hidden style={{ backgroundImage: 'radial-gradient(circle at 30% 30%, #f97316 0, transparent 50%), radial-gradient(circle at 70% 70%, #fb923c 0, transparent 50%)' }} />
            <div className="relative">
              <Sparkles className="w-10 h-10 mx-auto mb-4 text-orange-400" />
              <h2 className="text-3xl lg:text-4xl font-bold">Profitez d&apos;un mois d&apos;essai</h2>
              <p className="text-slate-300 mt-3 max-w-xl mx-auto">
                Pas de carte bancaire, pas d&apos;engagement. Testez toutes les fonctionnalités pendant
                <strong className="text-orange-400"> 30 jours</strong> et choisissez ensuite les modules dont vous avez besoin.
              </p>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <Link to="/register" onClick={() => onCtaRegister('pricing-section')}>
                  <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-7 py-6" data-testid="pricing-cta-btn">
                    Démarrer mon mois d&apos;essai <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <p className="text-xs text-slate-400 mt-5">
                Vos données vous appartiennent. Exportables à tout moment en Excel ou format comptable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============== TRUST / PROOF ============== */}
      <section className="py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <Trust icon={Shield} title="Données sécurisées" desc="HTTPS, bcrypt, isolation stricte par compte. Hébergement européen." />
            <Trust icon={Globe} title="100% en français" desc="Conçu pour la France et la Belgique. Conformité légale (FEC, Peppol)." />
            <Trust icon={Headphones} title="Support réactif" desc="Une question ? On répond sous 24h ouvrées par email." />
          </div>
        </div>
      </section>

      {/* ============== À PROPOS ============== */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <p className="text-orange-600 font-semibold uppercase text-xs tracking-widest">À propos</p>
            <h2 className="text-3xl lg:text-4xl font-bold mt-2 text-slate-900">
              Une histoire née <span className="text-orange-500">sur le terrain</span>
            </h2>
          </div>

          <div className="relative rounded-3xl bg-gradient-to-br from-orange-50 via-white to-amber-50 border border-orange-100 p-8 sm:p-12 shadow-sm">
            <div className="absolute -top-4 left-8 bg-orange-500 text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
              Le mot du fondateur
            </div>

            <div className="space-y-5 text-slate-700 leading-relaxed text-base sm:text-lg" data-testid="about-content">
              <p>
                Du haut de mes <strong className="text-slate-900">22 ans</strong>, j&apos;ai décidé de créer une solution
                <strong className="text-slate-900"> modulable à chaque entrepreneur</strong>, à son image.
              </p>
              <p>
                Lors de chacune des conversations que j&apos;ai pu mener avec différents entrepreneurs, quel que soit le corps de métier,
                une réflexion refaisait toujours surface : celle d&apos;
                <strong className="text-slate-900">outils informatiques trop peu complets</strong>.
                Certains sont bons sur un ou plusieurs points mais catastrophiques sur d&apos;autres — et inversement.
              </p>
              <p>
                Alors, pour pallier ces défaillances, je me suis interrogé. Moi, et différents corps de métier :
                un maçon, un élagueur-grimpeur, un jardinier, une agence de communication…
              </p>
              <p>
                Grâce à toutes ces rencontres, ces enrichissements, aux avancées informatiques actuelles, et enfin convaincu
                que toutes les cartes existent aujourd&apos;hui pour que la
                <strong className="text-slate-900"> gestion électronique de votre activité soit enfin un jeu d&apos;enfants</strong>…
              </p>
              <p className="text-xl sm:text-2xl font-bold text-slate-900 pt-2">
                Devis.Go est né <span className="text-orange-500">grâce à vous</span>,
                grandira <span className="text-orange-500">avec vous</span>,
                mais surtout, <span className="text-orange-500">pour vous</span>.
              </p>
            </div>

            <div className="mt-8 flex items-center gap-4 pt-6 border-t border-orange-200/60">
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_0864990c-3103-4980-899d-ee39c5c04e1b/artifacts/yvx5u2d4_T%C3%AAte%20de%20con%20un%20peu%20moins.png"
                alt="Stéphane Ligot, fondateur de Devis.Go"
                loading="lazy"
                className="w-14 h-14 rounded-full object-cover ring-2 ring-orange-200 shadow-md shrink-0"
                data-testid="about-founder-photo"
              />
              <div>
                <p className="font-semibold text-slate-900">Stéphane Ligot</p>
                <p className="text-sm text-slate-500">Fondateur de Devis.Go</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== FAQ ============== */}
      <section id="faq" className="py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="text-orange-600 font-semibold uppercase text-xs tracking-widest">FAQ</p>
            <h2 className="text-3xl lg:text-4xl font-bold mt-2">Questions fréquentes</h2>
          </div>
          <div className="divide-y divide-slate-200 border-y border-slate-200" data-testid="faq-list">
            {FAQ.map((item, i) => {
              const open = openFaq === i;
              return (
                <button
                  key={i}
                  className="w-full text-left py-5 flex items-start gap-4 hover:bg-slate-50 px-2 transition"
                  onClick={() => setOpenFaq(open ? -1 : i)}
                  data-testid={`faq-item-${i}`}
                >
                  <div className="flex-1">
                    <p className="font-semibold">{item.q}</p>
                    {open && <p className="text-slate-600 mt-2 text-sm leading-relaxed">{item.a}</p>}
                  </div>
                  {open ? <ChevronUp className="w-5 h-5 text-slate-400 mt-0.5" /> : <ChevronDown className="w-5 h-5 text-slate-400 mt-0.5" />}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="py-20 bg-gradient-to-br from-orange-500 to-orange-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold">Prêt à arrêter de bricoler dans Word ?</h2>
          <p className="mt-3 text-orange-50 text-lg">Créez votre premier devis pro en moins de 5 minutes.</p>
          <Link to="/register" onClick={() => onCtaRegister('footer')}>
            <Button size="lg" className="mt-7 bg-white text-orange-600 hover:bg-orange-50 px-7 py-6 text-base shadow-xl" data-testid="footer-register-btn">
              Créer mon compte gratuit <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <p className="mt-3 text-sm text-orange-100">1 mois d&apos;essai · sans carte bancaire</p>
        </div>
      </section>

      {/* ============== FOOTER ============== */}
      <footer className="py-12 bg-slate-900 text-slate-400 text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap items-start justify-between gap-8">
            <div>
              <img src="/logo-devisgo.png" alt="Devis.Go" className="h-10 mb-3 brightness-0 invert opacity-90" />
              <p className="max-w-xs leading-relaxed">Devis, factures et CRM pour indépendants et PME francophones.</p>
            </div>
            <div className="flex gap-12">
              <div>
                <p className="text-white font-semibold mb-3">Produit</p>
                <ul className="space-y-2">
                  <li><a href="#features" className="hover:text-orange-400">Fonctionnalités</a></li>
                  <li><a href="#faq" className="hover:text-orange-400">FAQ</a></li>
                </ul>
              </div>
              <div>
                <p className="text-white font-semibold mb-3">Compte</p>
                <ul className="space-y-2">
                  <li><Link to="/login" className="hover:text-orange-400">Connexion</Link></li>
                  <li><Link to="/register" className="hover:text-orange-400">Inscription</Link></li>
                </ul>
              </div>
              <div data-testid="footer-legal-block">
                <p className="text-white font-semibold mb-3">Contact & mentions légales</p>
                <ul className="space-y-2 not-italic">
                  <li className="text-slate-300">Devis.Go</li>
                  <li>Rue des Récollets, Rochefort</li>
                  <li>
                    <a href="tel:+32479334793" className="hover:text-orange-400" data-testid="footer-phone">
                      +32 479 33 47 93
                    </a>
                  </li>
                  <li>
                    <a href="mailto:stephane.devisgo@gmail.com" className="hover:text-orange-400" data-testid="footer-email">
                      stephane.devisgo@gmail.com
                    </a>
                  </li>
                  <li className="text-xs text-slate-500 pt-1">TVA : BE1041.160.683</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-slate-800 flex flex-wrap justify-between gap-3 text-xs">
            <p>© {new Date().getFullYear()} Devis.Go — Tous droits réservés</p>
            <p>Made with care, in French</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const Stat = ({ label, value, suffix }) => (
  <div>
    <p className="text-3xl lg:text-4xl font-bold text-slate-900">
      {Number(value || 0).toLocaleString('fr-FR')}{suffix}
    </p>
    <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">{label}</p>
  </div>
);

const Trust = ({ icon: Icon, title, desc }) => (
  <div className="bg-white rounded-2xl p-6 border border-slate-200">
    <div className="w-12 h-12 mx-auto rounded-xl bg-orange-100 flex items-center justify-center">
      <Icon className="w-6 h-6 text-orange-600" />
    </div>
    <h3 className="font-semibold mt-4">{title}</h3>
    <p className="text-slate-600 text-sm mt-1.5">{desc}</p>
  </div>
);

export default Landing;
